import React from 'react';
import {
  Activity, BarChart3, DollarSign, Eye, FileText, Globe, Mail, MousePointerClick, Search, ShoppingCart,
} from 'lucide-react';
import { BarViz, DailyActivityChart, KpiCard, LoadingSkeleton } from './_shared';
import WeeklyDigestCard from './WeeklyDigestCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EXTERNAL_TOOLS = [
  { name: 'Google Analytics', icon: BarChart3, color: '#F59E0B', url: 'https://analytics.google.com' },
  { name: 'Search Console', icon: Search, color: '#10B981', url: 'https://search.google.com/search-console' },
  { name: 'HubSpot CRM', icon: Mail, color: '#F97316', url: 'https://app.hubspot.com' },
  { name: 'Stripe Dashboard', icon: DollarSign, color: '#8B5CF6', url: 'https://dashboard.stripe.com' },
  { name: 'Resend', icon: Mail, color: '#3B82F6', url: 'https://resend.com/emails' },
];

export default function OverviewTab({ data }) {
  if (!data) return <LoadingSkeleton />;
  const categoryItems = (data.category_views || []).slice(0, 6).map(c => ({
    label: c.category, value: `${c.views} views`, raw: c.views
  }));
  const maxCatViews = Math.max(...categoryItems.map(c => c.raw), 1);

  // Build daily chart data
  const dailyChartData = (data.daily_activity || []).map(d => ({
    date: d.date,
    value: d.views + d.clicks + d.page_views,
    color: '#14b8a6',
  }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KpiCard icon={DollarSign} label="Total Revenue" value={`$${data.total_revenue?.toFixed(2) || '0'}`} subtext={`${data.total_orders} orders`} color="#10B981" />
        <KpiCard icon={MousePointerClick} label="Affiliate Clicks" value={data.affiliate_clicks || 0} subtext="tracked clicks" color="#06B6D4" />
        <KpiCard icon={Eye} label="Page Views" value={data.page_views || 0} subtext="tracked sessions" color="#F59E0B" />
        <KpiCard icon={FileText} label="Total Articles" value={data.total_articles} subtext="published" color="#8B5CF6" />
        <KpiCard icon={Mail} label="Total Leads" value={data.total_leads} subtext="captured" color="#3B82F6" />
        <KpiCard icon={ShoppingCart} label="Total Carts" value={data.total_carts} subtext={`${data.total_products} products`} color="#F97316" />
      </div>

      {/* Daily Activity Chart */}
      <DailyActivityChart data={dailyChartData} label="Daily Activity (Views + Clicks + Page Views)" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <Eye className="w-4 h-4 text-teal-500" /> Top Articles by Views
          </h3>
          {data.top_articles?.length > 0 ? (
            <div className="space-y-2.5">
              {data.top_articles.slice(0, 8).map((a, i) => (
                <div key={a.title} className="flex items-center justify-between text-sm py-1.5 border-b border-gray-50 last:border-0">
                  <span className="text-gray-700 truncate mr-3">{a.title}</span>
                  <span className="text-gray-400 font-mono text-xs whitespace-nowrap">{a.view_count || 0} views</span>
                </div>
              ))}
            </div>
          ) : <p className="text-gray-400 text-sm">No article views yet</p>}
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <Globe className="w-4 h-4 text-teal-500" /> Views by Category
          </h3>
          {categoryItems.length > 0 ? (
            <BarViz items={categoryItems} maxVal={maxCatViews} />
          ) : <p className="text-gray-400 text-sm">No data yet</p>}
        </div>
      </div>

      {/* Weekly Digest Settings */}
      <WeeklyDigestCard token={data._token} />

      {/* External Tools */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">External Tools</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {EXTERNAL_TOOLS.map((tool) => (
            <a
              key={tool.name}
              href={tool.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2.5 p-3 rounded-lg border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all group"
              data-testid={`external-tool-${tool.name.toLowerCase().replace(/\s/g, '-')}`}
            >
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${tool.color}15` }}>
                <tool.icon className="w-4 h-4" style={{ color: tool.color }} />
              </div>
              <span className="text-xs font-medium text-gray-700 group-hover:text-gray-900">{tool.name}</span>
            </a>
          ))}
        </div>
      </div>

      {/* Recent Leads */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <Mail className="w-4 h-4 text-blue-500" /> Recent Leads
        </h3>
        {data.recent_leads?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 text-xs border-b border-gray-100">
                  <th className="pb-2 font-medium">Name</th>
                  <th className="pb-2 font-medium">Email</th>
                  <th className="pb-2 font-medium">Source</th>
                  <th className="pb-2 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_leads.map((lead, i) => (
                  <tr key={lead.email} className="border-b border-gray-50 last:border-0">
                    <td className="py-2.5 text-gray-700">{lead.first_name} {lead.last_name}</td>
                    <td className="py-2.5 text-gray-500">{lead.email}</td>
                    <td className="py-2.5"><span className="px-2 py-0.5 bg-teal-50 text-teal-600 text-xs rounded-full">{lead.source || 'website'}</span></td>
                    <td className="py-2.5 text-gray-400 text-xs">{lead.created_at ? new Date(lead.created_at).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="text-gray-400 text-sm">No leads captured yet</p>}
      </div>
    </div>
  );
}
